import { useState, useCallback } from 'react';
import { fileApi, type FileRecord } from '../api/file_api';
import { fileAssociationApi } from '../api/file_association_api';

export interface PendingAttachment {
  id: string;
  file: File;
  status: 'uploading' | 'complete' | 'error';
  progress?: number;
  fileRecord?: FileRecord;
  error?: string;
}

export interface UseMessageAttachmentsReturn {
  attachments: PendingAttachment[];
  addFiles: (files: File[]) => Promise<void>;
  removeAttachment: (id: string) => void;
  clearAttachments: () => void;
  uploading: boolean;
  associateAll: (associations: Array<{ type: string; uuid: string }>) => Promise<void>;
}

export function useMessageAttachments(): UseMessageAttachmentsReturn {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);

  const addFiles = useCallback(async (files: File[]) => {
    for (const file of files) {
      const id = crypto.randomUUID();

      // Add to list as uploading
      setAttachments(prev => [...prev, { id, file, status: 'uploading' }]);

      try {
        const fileRecord = await fileApi.uploadFile(file, 'attachment');
        setAttachments(prev =>
          prev.map(a => (a.id === id ? { ...a, status: 'complete', fileRecord } : a))
        );
      } catch (err) {
        setAttachments(prev =>
          prev.map(a =>
            a.id === id
              ? { ...a, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' }
              : a
          )
        );
      }
    }
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const uploading = attachments.some(a => a.status === 'uploading');

  const associateAll = useCallback(
    async (associations: Array<{ type: string; uuid: string }>) => {
      const completedAttachments = attachments.filter(
        a => a.status === 'complete' && a.fileRecord
      );

      for (const attachment of completedAttachments) {
        for (const { type, uuid } of associations) {
          await fileAssociationApi.create(attachment.fileRecord!.uuid, type, uuid);
        }
      }
    },
    [attachments]
  );

  return {
    attachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    uploading,
    associateAll,
  };
}
